(function ($, window, document) {
    'use strict';

    /**
     * Configuration object for user account restoration flow.
     *
     * @property {string} baseUrl - The base API URL for restoration-related requests. Defaults to '/user' or the value provided by `window.WayGenAI?.apiBase`.
     *
     * @property {Object} i18n - Internationalization strings used to display messages in the restoration process.
     * @property {string} i18n.verifying - Message displayed while the account restoration process is being verified.
     * @property {string} i18n.successTitle - Title displayed when the account restoration is successful.
     * @property {string} i18n.successBody - Body text displayed when the account restoration is successful.
     * @property {string} i18n.expiredTitle - Title displayed when the link used for restoration has expired.
     * @property {string} i18n.expiredBody - Body text displayed when the link used for restoration has expired.
     * @property {string} i18n.invalidTitle - Title displayed when the link used for restoration is invalid.
     * @property {string} i18n.invalidBody - Body text displayed when the link used for restoration is invalid or already used.
     * @property {string} i18n.requestTitle - Title displayed after a new restore link has been requested.
     * @property {string} i18n.requestBody - Body text displayed after a new restore link has been requested if the account is inactive.
     * @property {string} i18n.invalidEmail - Message displayed when the entered email address is invalid.
     * @property {string} i18n.notFoundOrActive - Message displayed when the user account is not found or already active.
     * @property {string} i18n.fallback - Generic error message displayed when an unexpected error occurs.
     * @property {string} i18n.sendLink - Text for the button or link to send a restore link.
     * @property {string} i18n.login - Text for the login button or link.
     * @property {string} i18n.goAccount - Text for navigating to the user's account page.
     */
    const CFG = {
        baseUrl: window.WayGenAI?.apiBase || '/user',
        i18n: Object.assign({
            verifying: 'Restoring…',
            successTitle: 'Account restored:',
            successBody: 'Your account is active now. You can log in.',
            expiredTitle: 'Link expired:',
            expiredBody: 'Please request a new restore link.',
            invalidTitle: 'Invalid link:',
            invalidBody: 'The link is invalid or already used.',
            requestTitle: 'Check your email:',
            requestBody: 'If this inactive account exists, we sent a restore link to your email.',
            invalidEmail: 'Please enter a valid email address.',
            notFoundOrActive: 'User not found or already active.',
            fallback: 'Something went wrong. Please try again.',
            sendLink: 'Send restore link',
            login: 'Log in',
            goAccount: 'Go to account'
        }, window.WayGenAI?.i18n || {})
    };

    /**
     * Extracts the value of a query parameter from the current window's URL.
     *
     * @param {string} name The name of the query parameter to retrieve.
     * @return {string|null} The value of the query parameter if found, or null if the parameter does not exist.
     */
    function q(name) {
        const m = new RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
        return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : null;
    }

    /**
     * Validates if the given input is a properly formatted email address.
     *
     * @param {string} v - The string to be validated as an email address.
     * @return {boolean} Returns true if the input is a valid email address, false otherwise.
     */
    function isEmail(v) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    }

    /**
     * Toggles the loading state of a button by enabling/disabling it and showing/hiding a spinner.
     *
     * @param {jQuery} $btn - The button element wrapped in a jQuery object.
     * @param {boolean} on - Indicates whether the button should be in a loading state.
     * @return {void} This method does not return any value.
     */
    function setLoading($btn, on) {
        $btn.prop('disabled', on);
        $btn.find('.spinner').toggle(on);
    }

    /**
     * Displays a message in the status container while hiding other status containers.
     *
     * @param {string} msg - The message to be displayed in the status container.
     * @return {void} Does not return a value.
     */
    function showInfo(msg) {
        $('#re-status').text(msg).show().attr('aria-live', 'polite');
        $('#re-success, #re-error').hide().text('');
    }

    /**
     * Displays a success message in the designated UI component and hides any previously shown status or error messages.
     *
     * @param {string} title The title of the success message to be displayed.
     * @param {string} body The body or detailed description of the success message to be displayed.
     * @return {void} Does not return any value.
     */
    function showSuccess(title, body) {
        $('#re-status').hide().text('');
        $('#re-error').hide().text('');
        $('#re-success').html('<strong>' + title + '</strong> ' + body).show().attr('aria-live', 'polite');
    }

    /**
     * Displays an error message in the designated error message container on the webpage.
     *
     * @param {string} title The title of the error message, typically a short summary of the issue.
     * @param {string} body The main content or detailed explanation of the error message.
     * @return {void} This function does not return a value.
     */
    function showError(title, body) {
        $('#re-status').hide().text('');
        $('#re-success').hide().text('');
        $('#re-error').html('<strong>' + title + '</strong> ' + body).show().attr('aria-live', 'assertive');
    }

    // mode switch
    (function init() {
        const token = q('token');
        if (token) {
            $('#wg-restore-request-form').hide();
            $('#re-token-panel').show();
            doRestore(token);
        } else {
            $('#wg-restore-request-form').show();
            $('#re-token-panel').hide();
            $('#re-email').trigger('focus');
        }
    })();

    // submit: send restore link (POST /restore-request?email=...)
    $(document).on('submit', '#wg-restore-request-form', function (e) {
        e.preventDefault();
        $('#re-request-global').hide().text('');
        $('#re-request-success').hide().text('');
        $('#re-email').removeClass('is-invalid');
        $('[data-error-for="re-email"]').text('');

        const $btn = $('#re-request-submit');
        const email = $('#re-email').val().trim();

        if (!isEmail(email)) {
            $('[data-error-for="re-email"]').text(CFG.i18n.invalidEmail);
            $('#re-email').addClass('is-invalid');
            return;
        }

        setLoading($btn, true);

        $.ajax({
            url: (CFG.baseUrl.replace(/\/+$/, '') + '/restore-request?email=' + encodeURIComponent(email)),
            method: 'POST',
            dataType: 'json'
        })
            .done(function () {
                $('#re-request-success').text(CFG.i18n.requestTitle + ' ' + CFG.i18n.requestBody).show();
                $('#re-email').val('').trigger('input').trigger('change');
            })
            .fail(function (jqXHR) {
                const status = jqXHR.status;
                if (status === 404) {
                    $('#re-request-global').text(CFG.i18n.notFoundOrActive).show();
                    return;
                }
                $('#re-request-global').text(jqXHR.responseJSON?.detail || CFG.i18n.fallback).show();
            })
            .always(function () {
                setLoading($btn, false);
            });
    });

    // token flow
    function doRestore(token) {
        if (!token) {
            showError(CFG.i18n.invalidTitle, CFG.i18n.invalidBody);
            return;
        }
        showInfo(CFG.i18n.verifying);

        $.ajax({
            url: (CFG.baseUrl.replace(/\/+$/, '') + '/restore?token=' + encodeURIComponent(token)),
            method: 'POST'
        })
            .done(function () {
                showSuccess(CFG.i18n.successTitle, CFG.i18n.successBody);
            })
            .fail(function (jqXHR) {
                const status = jqXHR.status;
                const detail = (jqXHR.responseJSON?.detail || '').toLowerCase();
                if (status === 400 || detail.includes('expired')) {
                    showError(CFG.i18n.expiredTitle, CFG.i18n.expiredBody);
                    return;
                }
                if (status === 404 || detail.includes('invalid')) {
                    showError(CFG.i18n.invalidTitle, CFG.i18n.invalidBody);
                    return;
                }
                showError(CFG.i18n.invalidTitle, CFG.i18n.fallback);
            });
    }

    // clear field error on input
    $(document).on('input', '#re-email', function () {
        $(this).removeClass('is-invalid');
        $('[data-error-for="re-email"]').text('');
        $('#re-request-global').hide().text('');
    });

})(jQuery, window, document);
